import type { SightglassPageType } from "./sightglassOffers";

import { z } from "zod";

import { projectPersonaAwarenessSchema } from "./projectContext";
import {
  sightglassAgeBandSchema,
  sightglassAngleTypeSchema,
  sightglassFunnelStageSchema,
  sightglassHookTypeSchema,
  sightglassOfferMechanicSchema,
  sightglassOfferTagSchema,
  sightglassOfferTypeSchema,
  sightglassPageTypeSchema,
} from "./sightglassOffers";
import { sightglassLockSchema } from "./sightglassPlans";
import { sightglassResearchStatusSchema } from "./sightglassResearch";

/** Sightglass wire contracts — what the miniapp, the admin tool and agents see. */

export const sightglassAdDisplayFormatSchema = z.enum([
  "image",
  "video",
  "carousel",
  "dco",
  "dpa",
  "text",
  "other",
]);

/** The stored landing-page type. `product` reads as "PDP"; `squeeze` and `other` are legacy values nothing new writes. */
export const sightglassSitePageTypeSchema = z.enum([
  "product",
  "collection",
  "advertorial",
  "listicle",
  "quiz",
  "homepage",
  "squeeze",
  "other",
  "offer_page",
  "lead_capture",
  "blog",
  "marketplace",
  "app_store",
  "social_profile",
]);
export type SightglassSitePageTypeValue = z.infer<
  typeof sightglassSitePageTypeSchema
>;

/** A stored page type in the reading's vocabulary; `other` is a page not classified yet. */
export function getPageTypeFromSitePageType(
  value: SightglassSitePageTypeValue,
): SightglassPageType | null {
  if (value === "product") {
    return "pdp";
  }
  if (value === "squeeze") {
    return "lead_capture";
  }
  if (value === "other") {
    return null;
  }
  return value;
}

/** Every stored value one page type covers, legacy ones included, for filters. */
export function getSitePageTypesForPageType(
  pageType: SightglassPageType,
): SightglassSitePageTypeValue[] {
  if (pageType === "pdp") {
    return ["product"];
  }
  if (pageType === "lead_capture") {
    return ["lead_capture", "squeeze"];
  }
  return [pageType];
}

export const sightglassListKindSchema = z
  .enum(["company", "ad", "site_page", "persona"])
  .describe("SightglassListKind");

/** The display format with Meta's dynamic and catalog labels resolved from the stored media. */
export const sightglassAdTypeSchema = z
  .enum(["image", "video", "carousel"])
  .describe("SightglassAdType");

export const SIGHTGLASS_AD_TYPE_LABELS: Record<
  z.infer<typeof sightglassAdTypeSchema>,
  string
> = { image: "Image", video: "Video", carousel: "Carousel" };

export const sightglassAdTagsWireSchema = z
  .object({
    hookType: sightglassHookTypeSchema
      .nullable()
      .describe("Null when the reading is less sure than the tag threshold"),
    hookLine: z
      .string()
      .nullable()
      .describe("The opening line the hook type was read from"),
    angleType: sightglassAngleTypeSchema
      .nullable()
      .describe("Null when the reading is less sure than the tag threshold"),
    angleName: z.string().nullable(),
    personaNickname: z
      .string()
      .nullable()
      .describe("Who the angle speaks to, e.g. 'The weeknight cook'"),
    offerTags: z.array(sightglassOfferTagSchema),
    pageType: sightglassPageTypeSchema
      .nullable()
      .describe("The landing page's type; null without a classified page"),
    funnelStage: sightglassFunnelStageSchema,
    adType: sightglassAdTypeSchema,
  })
  .describe("SightglassAdTagsWire");

export const sightglassCreativeAssetTypeSchema = z.enum(["image", "video"]);
export const sightglassCreativeAssetStatusSchema = z.enum([
  "pending",
  "stored",
  "failed",
]);

export const sightglassRenderStatusSchema = z.enum([
  "pending",
  "rendered",
  "failed",
]);

export const sightglassTrafficSchema = z
  .object({
    monthlyVisits: z.string().nullable(),
    trafficRank: z.number().int().nullable(),
    authorityScore: z.number().int().nullable(),
    trend7d: z.number().nullable(),
  })
  .nullable()
  .describe("SightglassTraffic");

export const sightglassAdWireSchema = z
  .object({
    id: z.string(),
    adArchiveId: z.string(),
    company: z
      .object({
        id: z.string(),
        slug: z.string(),
        domain: z.string(),
        name: z.string(),
        logoUrl: z.string().nullable(),
      })
      .nullish()
      .describe(
        "The brand this ad counts for: the Meta Page's company when attached, else the landing page's.",
      ),
    advertiser: z
      .object({
        id: z.string(),
        name: z.string(),
        logoUrl: z.string().nullable(),
      })
      .describe(
        "The Meta page that ran the ad. Source metadata, not the brand.",
      ),
    sitePage: z
      .object({
        id: z.string(),
        url: z.string(),
        domain: z.string(),
        pageType: sightglassSitePageTypeSchema,
        screenshotDesktopUrl: z.string().nullable(),
      })
      .nullable(),
    displayFormat: sightglassAdDisplayFormatSchema,
    title: z.string().nullable(),
    body: z.string().nullable(),
    ctaType: z.string().nullable(),
    publisherPlatforms: z.array(z.string()),
    adDeliveryStartAt: z.string().nullable(),
    adDeliveryStopAt: z.string().nullable(),
    isActive: z.boolean(),
    lastSeenAt: z
      .string()
      .describe("When research last saw this ad in Meta's library."),
    statusSource: z
      .enum(["meta", "last_seen"])
      .describe(
        "`meta` when isActive is Meta's own word. `last_seen` when the brand's inventory exceeds the research cap and this ad has dropped out of the pulled top ads: it may or may not still run, and the UI says last seen rather than running.",
      ),
    daysActive: z.number().int(),
    duplicationCount: z.number().int(),
    variantCount: z
      .number()
      .int()
      .describe(
        "Visible ads running this creative, including this one. When the feed is collapsed this is how many rows the card stands in for; duplicationCount is the broader reuse signal and also folds in Meta's collation count.",
      ),
    performanceScore: z
      .number()
      .int()
      .describe(
        "Transparent 0-100 heuristic from run length + creative duplication. Not spend or ROAS.",
      ),
    creative: z.object({
      id: z.string(),
      collationId: z.string().nullable(),
      adCount: z.number().int(),
      advertiserCount: z.number().int(),
      creativeCaption: z.string().nullable(),
    }),
    assets: z.array(
      z.object({
        id: z.string(),
        type: sightglassCreativeAssetTypeSchema,
        status: sightglassCreativeAssetStatusSchema.describe(
          "pending until the media is downloaded; failed when the source URL is gone.",
        ),
        url: z
          .string()
          .nullable()
          .describe("Presigned, ~1h TTL; null unless status is stored."),
        thumbnailUrl: z.string().nullable(),
        width: z.number().int().nullable(),
        height: z.number().int().nullable(),
        durationSeconds: z.number().int().nullable(),
      }),
    ),
    savedByMe: z.boolean(),
    boardIds: z.array(z.string()),
    similarity: z
      .number()
      .nullable()
      .describe(
        "Cosine similarity for semantic search results; null otherwise.",
      ),
    tags: sightglassAdTagsWireSchema
      .nullable()
      .describe(
        "Hook, angle, offers, page type, funnel stage and ad type, from the creative's reading; null until the creative is read at the current version.",
      ),
    createdAt: z.string(),
  })
  .describe("SightglassAdWire");

export const sightglassAdvertiserWireSchema = z
  .object({
    id: z.string(),
    metaPageId: z.string(),
    name: z.string(),
    logoUrl: z.string().nullable(),
    pageVerified: z.boolean(),
    pageCreatedAt: z.string().nullable(),
    instagramHandle: z.string().nullable(),
    companyId: z.string().nullable(),
    companySlug: z.string().nullable(),
    createdAt: z.string(),
  })
  .describe("SightglassAdvertiserWire");

export const sightglassSitePageWireSchema = z
  .object({
    id: z.string(),
    url: z.string(),
    domain: z.string(),
    pageType: sightglassSitePageTypeSchema,
    title: z.string().nullable(),
    description: z.string().nullable(),
    screenshotDesktopUrl: z.string().nullable(),
    screenshotMobileUrl: z.string().nullable(),
    screenshotMobileFoldUrl: z
      .string()
      .nullable()
      .describe(
        "The first phone screen of the mobile capture, as WebP; what walls should load instead of the full page.",
      ),
    htmlUrl: z.string().nullable(),
    inspirationImportedAt: z
      .string()
      .nullable()
      .describe(
        "Set while this page's sections are in the inspiration registry; the debug overlay's Add/Remove reads it.",
      ),
    technologies: z
      .array(
        z.object({
          technology: z.string(),
          category: z.string(),
          confidence: z.number(),
          logoUrl: z
            .string()
            .nullable()
            .describe(
              "Presigned mirror of the vendor's mark from the technology registry; null → render by name.",
            ),
        }),
      )
      .describe("Detected on the company's pages and combined, not per-page."),
    activeAdsCount: z.number().int(),
    traffic: sightglassTrafficSchema,
    locked: z
      .array(sightglassLockSchema)
      .describe(
        "Data the caller's plan withholds; those fields are null. Empty when nothing is withheld.",
      ),
    company: z
      .object({
        id: z.string(),
        slug: z.string(),
        name: z.string(),
        logoUrl: z.string().nullable(),
        activeAdsCount: z.number().int(),
      })
      .describe("The company (hostname) this landing page belongs to."),
    savedByMe: z.boolean(),
    boardIds: z.array(z.string()),
    bestAdScore: z
      .number()
      .int()
      .nullable()
      .describe(
        "The highest performance score among ads pointing here. Null when no ad points at this page — a page is only as interesting as the best funnel running to it.",
      ),
    renderStatus: sightglassRenderStatusSchema,
    similarity: z.number().nullable(),
    createdAt: z.string(),
  })
  .describe("SightglassSitePageWire");

export const sightglassFilterOptionsWireSchema = z
  .object({
    technologies: z.array(
      z.object({
        technology: z.string(),
        category: z.string(),
        companyCount: z.number().int(),
      }),
    ),
    categories: z
      .array(
        z.object({
          id: z.string(),
          parentId: z.string().nullable(),
          name: z.string(),
          path: z.string(),
          depth: z.number().int(),
          companyCount: z.number().int(),
          subtreeCompanyCount: z.number().int(),
        }),
      )
      .describe(
        "The taxonomy tree as a flat list, roots first by subtree size; a root is a node with no parent.",
      ),
    pageTypes: z.array(
      z.object({
        pageType: sightglassSitePageTypeSchema,
        sitePageCount: z.number().int(),
      }),
    ),
    displayFormats: z.array(
      z.object({
        displayFormat: sightglassAdDisplayFormatSchema,
        adCount: z.number().int(),
      }),
    ),
    publisherPlatforms: z.array(
      z.object({ publisherPlatform: z.string(), adCount: z.number().int() }),
    ),
    domains: z
      .array(z.object({ domain: z.string(), adCount: z.number().int() }))
      .describe("Top 100 brand domains by ad count."),
    tagOptions: z
      .object({
        hookTypes: z.array(
          z.object({ value: sightglassHookTypeSchema, label: z.string() }),
        ),
        angleTypes: z.array(
          z.object({ value: sightglassAngleTypeSchema, label: z.string() }),
        ),
        offerTags: z.array(
          z.object({ value: sightglassOfferTagSchema, label: z.string() }),
        ),
        funnelStages: z.array(
          z.object({ value: sightglassFunnelStageSchema, label: z.string() }),
        ),
        pageTypes: z.array(
          z.object({
            value: sightglassPageTypeSchema,
            label: z.string(),
            isLandingPage: z
              .boolean()
              .describe("One of the dedicated landing-page types"),
          }),
        ),
        adTypes: z.array(
          z.object({ value: sightglassAdTypeSchema, label: z.string() }),
        ),
      })
      .describe(
        "Every value of the tag filters, in display order, with its label",
      ),
  })
  .describe("SightglassFilterOptionsWire");

export const sightglassBoardWireSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    isDefault: z
      .boolean()
      .describe("The user's Saved list; cannot be renamed or deleted."),
    kind: sightglassListKindSchema
      .nullable()
      .describe(
        "What the list holds. Null for the legacy mixed list, which accepts anything and belongs in every kind's picker.",
      ),
    itemCount: z
      .number()
      .int()
      .describe("Ads, landing pages, companies and personas on the list."),
    itemCounts: z.object({
      ads: z.number().int(),
      sitePages: z.number().int(),
      companies: z.number().int(),
      personas: z.number().int(),
    }),
    createdAt: z.string(),
  })
  .describe("SightglassBoardWire");

export const sightglassBoardItemSchema = z
  .union([
    z.object({ adId: z.uuid() }).strict(),
    z.object({ sitePageId: z.uuid() }).strict(),
    z.object({ companyId: z.uuid() }).strict(),
    z.object({ personaId: z.uuid() }).strict(),
  ])
  .describe("SightglassBoardItem");

export const sightglassSavedSearchNameSchema = z
  .string()
  .trim()
  .min(1, "Enter a name for this search.")
  .max(255, "Use 255 characters or fewer.");

export const sightglassSavedSearchWireSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    query: z.record(z.string(), z.unknown()),
    createdAt: z.string(),
  })
  .describe("SightglassSavedSearchWire");

/** `totalCount` is capped at 10,000 by the repositories: an exact count over the whole corpus costs a full scan per request. */
const totalCountSchema = z
  .number()
  .int()
  .describe(
    "Capped at 10000. Render the cap as '10,000+', never as the literal number.",
  );

export const sightglassAdListResultSchema = z
  .object({
    items: z.array(sightglassAdWireSchema),
    nextCursor: z.string().nullable(),
    totalCount: totalCountSchema,
    lockedCount: z
      .number()
      .int()
      .nonnegative()
      .describe(
        "Rows the caller's plan hides beyond this page; 0 when nothing is hidden.",
      ),
  })
  .describe("SightglassAdListResult");

export const sightglassSitePageListResultSchema = z
  .object({
    items: z.array(sightglassSitePageWireSchema),
    nextCursor: z.string().nullable(),
    totalCount: totalCountSchema,
    lockedCount: z
      .number()
      .int()
      .nonnegative()
      .describe(
        "Rows the caller's plan hides beyond this page; 0 when nothing is hidden.",
      ),
  })
  .describe("SightglassSitePageListResult");

export const sightglassBoardListResultSchema = z
  .object({
    items: z.array(sightglassBoardWireSchema),
    nextCursor: z.string().nullable(),
    totalCount: totalCountSchema,
  })
  .describe("SightglassBoardListResult");

export const sightglassSavedSearchListResultSchema = z
  .object({
    items: z.array(sightglassSavedSearchWireSchema),
    nextCursor: z.string().nullable(),
    totalCount: totalCountSchema,
  })
  .describe("SightglassSavedSearchListResult");

// --------------------------------------------------------------------------- Find queries — flat objects with CSV multi-values, because OpenAPI para...

export const SIGHTGLASS_DISCOVERY_ADS_PER_ADVERTISER = 4;

export const sightglassAdLaunchWindowSchema = z
  .enum(["7d", "30d", "90d"])
  .describe("SightglassAdLaunchWindow");

export const sightglassAdsQuerySchema = z
  .object({
    search: z
      .string()
      .max(200)
      .optional()
      .describe(
        "Free text. In semantic mode, describe the ad's angle, offer, or visual style — not just a keyword.",
      ),
    searchMode: z
      .enum(["semantic", "text", "hybrid"])
      .optional()
      .describe(
        "Defaults to hybrid: exact text matches rank first, then semantic matches fill the rest of the page. Use text for an exact-substring search, semantic to describe an ad you cannot name.",
      ),
    adsPerAdvertiserLimit: z.coerce
      .number()
      .int()
      .min(0)
      .max(100)
      .optional()
      .describe(
        "Show at most this many ads per advertiser so one prolific advertiser cannot fill the page. 0 is uncapped. When set, totalCount counts shown ads, not matching ads.",
      ),
    collapseVariants: z
      .stringbool()
      .optional()
      .describe(
        "Fold every ad running the same creative into one row carrying variantCount. Ignored when the query already targets a single creative.",
      ),
    advertiserId: z.uuid().optional(),
    sitePageId: z.uuid().optional(),
    displayFormats: z
      .string()
      .optional()
      .describe("CSV of: image,video,carousel,dco,dpa,text,other"),
    publisherPlatforms: z
      .string()
      .optional()
      .describe("CSV, e.g. FACEBOOK,INSTAGRAM"),
    languages: z.string().optional().describe("CSV of language codes"),
    pageTypes: z
      .string()
      .optional()
      .describe(
        "CSV of landing-page types: offer_page,advertorial,listicle,quiz,lead_capture,homepage,pdp,collection,blog,marketplace,app_store,social_profile. The stored names product, squeeze and other still work.",
      ),
    hookTypes: z
      .string()
      .optional()
      .describe(
        `CSV of hook types: ${sightglassHookTypeSchema.options.join(",")}`,
      ),
    angleTypes: z
      .string()
      .optional()
      .describe(
        `CSV of angle types: ${sightglassAngleTypeSchema.options.join(",")}`,
      ),
    offerTags: z
      .string()
      .optional()
      .describe(
        `CSV of offer tags: ${sightglassOfferTagSchema.options.join(",")}`,
      ),
    funnelStages: z
      .string()
      .optional()
      .describe("CSV of funnel stages: cold,warm,retargeting"),
    listId: z
      .uuid()
      .optional()
      .describe("Only ads on this ad list of the caller's"),
    companyListId: z
      .uuid()
      .optional()
      .describe(
        "Only ads of the companies on this company list of the caller's",
      ),
    technologies: z
      .string()
      .optional()
      .describe(
        "CSV of display names from filter-options, e.g. Shopify,Klaviyo",
      ),
    domains: z.string().optional().describe("CSV of brand domains"),
    categoryIds: z
      .string()
      .optional()
      .describe("CSV of category ids; a node matches its whole subtree"),
    companyIds: z.string().optional().describe("CSV of company ids"),
    status: z.enum(["active", "inactive"]).optional(),
    activityWindow: z
      .enum(["90d"])
      .optional()
      .describe(
        "Include active ads or ads whose reported delivery ended (or started when no end is known) within the last 90 days. Omit for all history.",
      ),
    launchWindow: sightglassAdLaunchWindowSchema
      .optional()
      .describe(
        "Only ads whose reported delivery started within this rolling window. An explicit adDeliveryStartAtGte takes precedence.",
      ),
    daysActiveGte: z.coerce.number().int().min(0).optional(),
    daysActiveLte: z.coerce.number().int().min(1).optional(),
    duplicationCountGte: z.coerce.number().int().min(0).optional(),
    scoreTiers: z
      .string()
      .optional()
      .describe(
        "CSV of performance tiers: testing (1-40), scaling (41-60), growing (61-80), optimized (81-90), winning (91+).",
      ),
    adDeliveryStartAtGte: z.string().optional(),
    adDeliveryStartAtLt: z.string().optional(),
    creativeId: z.uuid().optional().describe("All placements of one creative."),
    boardId: z
      .uuid()
      .optional()
      .describe(
        "Narrow to one of the caller's lists. Only meaningful on the saved-ads listing.",
      ),
    sort: z
      .enum(["newest", "longest_running", "performance", "most_duplicated"])
      .optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
  })
  .describe("SightglassAdsQuery");

export const sightglassSitePagesQuerySchema = z
  .object({
    search: z.string().max(200).optional(),
    searchMode: z.enum(["semantic", "text"]).optional(),
    domains: z.string().optional().describe("CSV of site domains"),
    categoryIds: z
      .string()
      .optional()
      .describe("CSV of category ids; a node matches its whole subtree"),
    pageTypes: z
      .string()
      .optional()
      .describe(
        "CSV of landing-page types, as on the ads listing; stored names still work",
      ),
    technologies: z.string().optional().describe("CSV of technology names"),
    activeAdsCountGte: z.coerce.number().int().min(0).optional(),
    companyListId: z
      .uuid()
      .optional()
      .describe(
        "Only pages of the companies on this company list of the caller's",
      ),
    boardId: z
      .uuid()
      .optional()
      .describe(
        "Narrow to one of the caller's lists. Only meaningful on the saved-pages listing.",
      ),
    sort: z
      .enum(["newest", "most_ads"])
      .optional()
      .describe("Defaults to newest, or most_ads when balance is set."),
    balance: z
      .stringbool()
      .optional()
      .describe(
        "Spread one page of at most 50 rows round-robin across the companies in scope (most active ads first), each contributing its pages in sort order. No next page; totalCount counts the balanced set. Ignored by semantic search.",
      ),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
  })
  .describe("SightglassSitePagesQuery");

export const sightglassCategoryWireSchema = z
  .object({
    id: z.string(),
    parentId: z.string().nullable(),
    name: z.string(),
    path: z.string(),
    depth: z.number().int(),
    companyCount: z.number().int(),
    subtreeCompanyCount: z.number().int(),
    ancestors: z.array(z.object({ id: z.string(), name: z.string() })),
  })
  .describe("SightglassCategoryWire");

export const sightglassCompanyEnrichmentStateSchema = z.enum([
  "none",
  "pending",
  "enriched",
  "not_found",
]);

export const sightglassMomentumLabelSchema = z.enum([
  "blowing_up",
  "rising",
  "steady",
  "cooling",
]);

export const sightglassCompanyMixSchema = z
  .object({
    adTypes: z.object({
      image: z.number().int(),
      video: z.number().int(),
      carousel: z.number().int(),
    }),
    pageTypes: z
      .partialRecord(
        z.enum([...sightglassPageTypeSchema.options, "unread"]),
        z.number().int(),
      )
      .describe(
        "Active ads by landing-page type; `unread` counts ads with no page or an unclassified one. Types with no ads are absent.",
      ),
    activeAds: z.number().int().describe("Active visible ads the mix covers"),
  })
  .describe("SightglassCompanyMix");

export const sightglassCompanyWireSchema = z
  .object({
    researchStatus: sightglassResearchStatusSchema
      .nullable()
      .describe(
        "Derived from the company's research timestamps: researching while a run is in flight, then its outcome. Null when never researched.",
      ),
    researchFinishedAt: z.string().nullable(),
    id: z.string(),
    slug: z.string(),
    domain: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    logoUrl: z
      .string()
      .nullable()
      .describe(
        "Presigned. Precedence: admin override → site icon → vendor icon → primary advertiser's Meta avatar → null (render a monogram).",
      ),
    category: sightglassCategoryWireSchema.nullable(),
    store: z.object({
      platform: z.string().nullable(),
      platformPlan: z.string().nullable(),
      since: z.number().int().nullable(),
      countryCode: z.string().nullable(),
      estimatedMonthlySalesText: z
        .string()
        .nullable()
        .describe(
          "Compact, e.g. '$668K'. An estimate, never reported revenue.",
        ),
      estimatedMonthlyVisitsText: z.string().nullable(),
      estimatedMonthlySales: z
        .number()
        .nullable()
        .describe(
          "Whole USD (the column behind it stores cents), unformatted, so a table can scale a whole column to one unit instead of formatting each row on its own.",
        ),
      estimatedMonthlyVisits: z.number().nullable(),
      productCount: z.number().int().nullable(),
      employeeCountText: z.string().nullable(),
      rank: z
        .number()
        .int()
        .nullable()
        .describe(
          "The store's rank among every store the vendor tracks; lower is bigger.",
        ),
      rankPercentile: z
        .number()
        .nullable()
        .describe(
          "Rank as a percent of all tracked stores; 1.05 reads as 'top 1.1%'.",
        ),
      categoryPaths: z.array(z.string()),
      apps: z.array(z.string()),
    }),
    socials: z.array(
      z.object({
        network: z.string(),
        handle: z.string(),
        url: z.string(),
        followers: z.number().int().nullable(),
        followers30d: z.number().int().nullable(),
      }),
    ),
    links: z.object({
      website: z.string().nullable(),
      adLibrary: z.string(),
    }),
    enrichment: sightglassCompanyEnrichmentStateSchema,
    enrichedAt: z.string().nullable(),
    activeAdsCount: z.number().int(),
    activeAds7dAgo: z.number().int(),
    activeAds30dAgo: z.number().int(),
    launchedAds7d: z.number().int(),
    launchedAds30d: z.number().int(),
    launchedAdsPrior30d: z.number().int(),
    totalAdsCount: z.number().int(),
    topGradeAdsCount: z.number().int(),
    rank: z.number().int().nullable(),
    rankDelta7d: z.number().int().nullable(),
    rankDelta30d: z.number().int().nullable(),
    categoryRank: z.number().int().nullable(),
    momentum: z.object({
      score: z.number().nullable(),
      label: sightglassMomentumLabelSchema.nullable(),
      components: z.array(
        z.object({
          key: z.string(),
          label: z.string(),
          percentile: z.number(),
          text: z.string(),
        }),
      ),
    }),
    gradeMix: z.array(
      z.object({ minScore: z.number().int(), adCount: z.number().int() }),
    ),
    videoAdsShare: z
      .number()
      .nullable()
      .describe(
        "0-1 share of the brand's ads that are video; null when it has none.",
      ),
    mediaMix: z.array(
      z.object({
        displayFormat: sightglassAdDisplayFormatSchema,
        adCount: z.number().int(),
      }),
    ),
    trend28d: z
      .array(z.number().int())
      .describe(
        "Active ads per day, oldest first; empty when the company has no ads.",
      ),
    traffic: sightglassTrafficSchema,
    locked: z
      .array(sightglassLockSchema)
      .describe(
        "Data the caller's plan withholds; those fields are null. Empty when nothing is withheld.",
      ),
    tracked: z
      .boolean()
      .describe(
        "On the research list. A saved company is always tracked; a catalog company may not be yet.",
      ),
    research: z
      .object({
        adCap: z
          .number()
          .int()
          .nullable()
          .describe("Admin override of the weekly pull cap; null = default."),
        aggregateOnly: z
          .boolean()
          .describe(
            "The brand's inventory exceeded the cap on its last pull: the active count is Meta's own, and ads outside the pulled top carry a last-seen status.",
          ),
        metaReportedActiveAds: z.number().int().nullable(),
      })
      .describe("SightglassCompanyResearch"),
    brandScore: z
      .number()
      .nullable()
      .describe("0-100 over the ThingTesting catalog; null until scored."),
    savedByMe: z.boolean(),
    boardIds: z.array(z.string()),
    newSinceLastSeen: z
      .number()
      .int()
      .nullable()
      .describe(
        "Ads first seen since the caller last opened the company. Null when it is not saved, or has never been opened.",
      ),
    similarity: z.number().nullable(),
    newestCreativeImageUrls: z
      .array(z.string())
      .nullable()
      .describe(
        "Signed stills of the company's newest running creatives, newest first. Null on every read except the caller's saved companies.",
      ),
    mix: sightglassCompanyMixSchema,
    createdAt: z.string(),
  })
  .describe("SightglassCompanyWire");

export const sightglassHomeSummaryWireSchema = z
  .object({
    corpus: z.object({
      adCount: z.number().int(),
      sitePageCount: z.number().int(),
      companyCount: z.number().int(),
      lastResearchedAt: z
        .string()
        .nullable()
        .describe(
          "When an ad was last seen by research. Null before the first run — the UI must say nothing rather than imply freshness.",
        ),
    }),
    trending: z.object({
      ads: z.array(sightglassAdWireSchema),
      basis: z
        .enum(["last_7_days", "all_time"])
        .describe(
          "Which window the ranking actually covers. Falls back to all-time when the trailing week is too thin to rank, so the UI can say which it is showing.",
        ),
    }),
    collections: z.object({
      topGradeAdCount: z.number().int(),
      quizFunnelCount: z.number().int(),
      topDuplicationCount: z.number().int(),
      newThisWeekCount: z.number().int(),
    }),
    savedCompanies: z.array(sightglassCompanyWireSchema),
    freshSitePages: z.array(sightglassSitePageWireSchema),
  })
  .describe("SightglassHomeSummaryWire");

export const sightglassCompanyListResultSchema = z
  .object({
    items: z.array(sightglassCompanyWireSchema),
    nextCursor: z.string().nullable(),
    totalCount: z.number().int(),
    lockedCount: z
      .number()
      .int()
      .nonnegative()
      .describe(
        "Rows the caller's plan hides beyond this page; 0 when nothing is hidden.",
      ),
  })
  .describe("SightglassCompanyListResult");

export const sightglassSavedSummaryWireSchema = z
  .object({
    adCount: z.number().int(),
    sitePageCount: z.number().int(),
    preview: z
      .array(
        z.object({
          kind: z.enum(["ad", "sitePage"]),
          id: z.uuid(),
          title: z.string().nullable(),
          /** The thumbnail to show; null falls back to `label`. */
          imageUrl: z.string().nullable(),
          /** Advertiser name for an ad, domain for a page. */
          label: z.string(),
        }),
      )
      .describe(
        "A glance at a few saved things: top saved ads first, then saved pages.",
      ),
  })
  .describe("SightglassSavedSummaryWire");

export const sightglassCompaniesQuerySchema = z
  .object({
    search: z.string().max(200).optional(),
    searchMode: z.enum(["hybrid", "text", "semantic"]).optional(),
    categoryIds: z
      .string()
      .optional()
      .describe("CSV of category ids; a node matches its whole subtree"),
    similarToCompanyId: z.uuid().optional(),
    activeAdsCountGte: z.coerce.number().int().min(0).optional(),
    platforms: z.string().optional().describe("CSV of store platform names"),
    countryCodes: z
      .string()
      .optional()
      .describe("CSV of ISO-3166 alpha-2 codes"),
    plans: z
      .string()
      .optional()
      .describe(
        "CSV of store plan names as the platform reports them, e.g. Shopify Plus",
      ),
    momentumLabels: z
      .string()
      .optional()
      .describe("CSV of Momentum labels: blowing_up, rising, steady, cooling"),
    technologies: z
      .string()
      .optional()
      .describe(
        "CSV of technology display names as the fingerprinter reports them, e.g. Klaviyo; a company matches when it carries any of them",
      ),
    sort: z
      .enum([
        "most_active_ads",
        "ad_growth_7d",
        "ad_growth_30d",
        "most_launched",
        "most_launched_7d",
        "most_top_grade_ads",
        "most_estimated_sales",
        "most_visits",
        "most_total_ads",
        "momentum",
        "newest",
        "name",
      ])
      .optional()
      .describe(
        "ad_growth_* orders by (active now - active N days ago) / max(active N days ago, 1)",
      ),
    sortDirection: z
      .enum(["asc", "desc"])
      .optional()
      .describe(
        "Reverses the sort's natural direction. Rows with no value stay at the tail either way.",
      ),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
  })
  .describe("SightglassCompaniesQuery");

export const sightglassTechnologiesQuerySchema = z
  .object({
    search: z.string().max(200).optional(),
    category: z
      .string()
      .optional()
      .describe("App category as the fingerprinter labels it, e.g. email-sms"),
    sort: z
      .enum([
        "most_companies",
        "top_100_share",
        "adopter_momentum",
        "adopter_ad_growth",
        "name",
      ])
      .optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
  })
  .describe("SightglassTechnologiesQuery");

export const sightglassTechnologyRankWireSchema = z
  .object({
    slug: z.string(),
    name: z.string(),
    category: z.string().nullable(),
    domain: z.string().nullable(),
    logoUrl: z
      .string()
      .nullable()
      .describe(
        "Presigned mirror of the vendor's mark; null → render by name.",
      ),
    companyCount: z
      .number()
      .int()
      .describe(
        "Companies with the vendor fingerprinted on their pages or installed on their store, per the registry.",
      ),
    adopterCount: z
      .number()
      .int()
      .describe(
        "Companies with the vendor fingerprinted on the pages their ads land on — the set every stat below is over.",
      ),
    adoptionShare: z
      .number()
      .describe("adopterCount over every ranked company, 0–1."),
    top100Count: z
      .number()
      .int()
      .describe("Adopters ranked in the top 100 by active ads."),
    top100Share: z.number().describe("top100Count over 100."),
    adopterMomentum: z
      .number()
      .nullable()
      .describe(
        "Mean Momentum score of the adopters; null when none is scored.",
      ),
    adopterAdGrowth30d: z
      .number()
      .describe(
        "Mean 30-day active-ad growth of the adopters, as a ratio of the count 30 days ago.",
      ),
    detectionName: z
      .string()
      .describe(
        "The fingerprinter's most common name for this vendor; the value to filter companies, ads and site pages by.",
      ),
    detectionNames: z
      .array(z.string())
      .describe(
        "Every name the fingerprinter uses for this vendor; `technologies` filters accept any of them.",
      ),
  })
  .describe("SightglassTechnologyRankWire");

export const sightglassTechnologyListResultSchema = z
  .object({
    items: z.array(sightglassTechnologyRankWireSchema),
    totalCount: z.number().int(),
    companyCount: z
      .number()
      .int()
      .describe(
        "Ranked companies in the corpus, the denominator behind adoptionShare.",
      ),
  })
  .describe("SightglassTechnologyListResult");

export const sightglassTechnologyDetailWireSchema = z
  .object({
    technology: sightglassTechnologyRankWireSchema,
    peers: z
      .array(sightglassTechnologyRankWireSchema)
      .describe(
        "Other vendors in the same category, by adoption, the vendor itself included.",
      ),
    companyCount: z.number().int(),
  })
  .describe("SightglassTechnologyDetailWire");

export const sightglassCompanyTrendWireSchema = z
  .object({
    firstSeenAt: z.string().nullable(),
    points: z.array(
      z.object({ date: z.string(), activeAdsCount: z.number().int() }),
    ),
    locked: z
      .array(sightglassLockSchema)
      .describe("Non-empty when the caller's plan withholds this chart."),
  })
  .describe("SightglassCompanyTrendWire");

export const sightglassTechStackItemWireSchema = z
  .object({
    slug: z.string(),
    name: z.string(),
    category: z.string().nullable(),
    domain: z.string().nullable(),
    logoUrl: z
      .string()
      .nullable()
      .describe(
        "Presigned mirror of the vendor's mark; null → render by name.",
      ),
    kind: z
      .enum(["detected", "app"])
      .describe(
        "`detected` = fingerprinted on the brand's pages; `app` = installed on the store per the vendor.",
      ),
    confidence: z.number().nullable(),
    installedAt: z.string().nullable(),
    companyCount: z
      .number()
      .int()
      .describe("How many tracked brands use it — the ordering key."),
  })
  .describe("SightglassTechStackItemWire");

export const sightglassCompanyTechStackWireSchema = z
  .object({
    items: z.array(sightglassTechStackItemWireSchema),
    detectedCount: z.number().int(),
    appCount: z.number().int(),
  })
  .describe("SightglassCompanyTechStackWire");

export const sightglassCompanyLaunchesWireSchema = z
  .object({
    weeks: z.array(
      z.object({ weekStart: z.string(), launchedAdsCount: z.number().int() }),
    ),
    locked: z
      .array(sightglassLockSchema)
      .describe("Non-empty when the caller's plan withholds this chart."),
  })
  .describe("SightglassCompanyLaunchesWire");

export const sightglassDaysActiveBucketsWireSchema = z
  .object({
    buckets: z.array(
      z.object({ label: z.string(), adCount: z.number().int() }),
    ),
    locked: z
      .array(sightglassLockSchema)
      .describe("Non-empty when the caller's plan withholds this chart."),
  })
  .describe("SightglassDaysActiveBucketsWire");

export const sightglassTopShareWireSchema = z
  .object({
    weeks: z.array(z.string()),
    series: z.array(
      z.object({
        companyId: z.string(),
        slug: z.string(),
        name: z.string(),
        logoUrl: z.string().nullable(),
        values: z.array(z.number()),
      }),
    ),
    others: z.array(z.number()),
  })
  .describe("SightglassTopShareWire");

export const sightglassCategoryShareWireSchema = z
  .object({
    rows: z.array(
      z.object({
        categoryId: z.string(),
        name: z.string(),
        activeAdsCount: z.number().int(),
        deltaPercent: z.number().nullable(),
      }),
    ),
  })
  .describe("SightglassCategoryShareWire");

export const sightglassShareGroupBySchema = z
  .enum(["category", "platform", "country", "plan", "company"])
  .describe("SightglassShareGroupBy");

export const sightglassGroupedShareWireSchema = z
  .object({
    groupBy: sightglassShareGroupBySchema,
    weeks: z
      .array(z.string())
      .describe("Sample dates, oldest first, one per week"),
    categoryDepth: z
      .number()
      .int()
      .describe(
        "For category grouping: the depth the series are drawn at (0 = roots)",
      ),
    series: z.array(
      z.object({
        key: z.string(),
        label: z.string(),
        categoryId: z.string().nullable(),
        slug: z
          .string()
          .nullable()
          .describe("Company slug when grouped by company"),
        logoUrl: z.string().nullable(),
        values: z.array(z.number().int()),
      }),
    ),
    others: z.array(z.number().int()),
  })
  .describe("SightglassGroupedShareWire");

/** Facet counts for the leaderboard's filter picker, scoped like the pulse. */
export const sightglassCompanyFilterOptionsWireSchema = z
  .object({
    categoryId: z.string().nullable(),
    platforms: z.array(
      z.object({ platform: z.string(), companyCount: z.number().int() }),
    ),
    countryCodes: z.array(
      z.object({ countryCode: z.string(), companyCount: z.number().int() }),
    ),
    plans: z.array(
      z.object({ plan: z.string(), companyCount: z.number().int() }),
    ),
    momentum: z.array(
      z.object({
        label: sightglassMomentumLabelSchema,
        companyCount: z.number().int(),
      }),
    ),
  })
  .describe("SightglassCompanyFilterOptionsWire");

export const sightglassMarketPulseWireSchema = z
  .object({
    categoryId: z.string().nullable(),
    companiesWithActiveAds: z.number().int(),
    activeAdsCount: z.number().int(),
    launched7d: z.number().int(),
    launchedPrior7d: z.number().int(),
    videoSharePercent: z.number().nullable(),
    advertorialSharePercent: z
      .number()
      .nullable()
      .describe(
        "Active ads landing on advertorial pages ÷ active ads with a known landing page",
      ),
    landingPageMix: z.array(
      z.object({
        pageType: sightglassSitePageTypeSchema,
        activeAdsCount: z.number().int(),
        sharePercent: z.number(),
      }),
    ),
    blowingUp: z.number().int(),
    rising: z.number().int(),
    cooling: z.number().int(),
  })
  .describe("SightglassMarketPulseWire");

export const sightglassCategoryLeadersWireSchema = z
  .object({
    categoryId: z.string(),
    name: z.string(),
    totalActiveAds: z.number().int(),
    rows: z.array(
      z.object({
        company: sightglassCompanyWireSchema,
        sharePercent: z.number(),
      }),
    ),
  })
  .describe("SightglassCategoryLeadersWire");

export const sightglassCompanyUpdateBodySchema = z
  .object({
    name: z.string().max(512).nullish(),
    description: z.string().max(5000).nullish(),
    logoUrl: z.string().max(1024).nullish(),
    categoryId: z.uuid().nullish(),
    researchAdCap: z
      .number()
      .int()
      .positive()
      .max(5000)
      .nullish()
      .describe(
        "Per-brand override of the weekly research pull cap; null returns the brand to the pipeline default.",
      ),
  })
  .strict()
  .describe("SightglassCompanyUpdateBody");

export type SightglassAdWire = z.infer<typeof sightglassAdWireSchema>;
export type SightglassAdvertiserWire = z.infer<
  typeof sightglassAdvertiserWireSchema
>;
export type SightglassSitePageWire = z.infer<
  typeof sightglassSitePageWireSchema
>;
export type SightglassFilterOptionsWire = z.infer<
  typeof sightglassFilterOptionsWireSchema
>;
export type SightglassBoardWire = z.infer<typeof sightglassBoardWireSchema>;
export type SightglassBoardItem = z.infer<typeof sightglassBoardItemSchema>;
export type SightglassTraffic = z.infer<typeof sightglassTrafficSchema>;
export type SightglassSavedSearchWire = z.infer<
  typeof sightglassSavedSearchWireSchema
>;
export type SightglassAdListResult = z.infer<
  typeof sightglassAdListResultSchema
>;
export type SightglassSitePageListResult = z.infer<
  typeof sightglassSitePageListResultSchema
>;
export type SightglassBoardListResult = z.infer<
  typeof sightglassBoardListResultSchema
>;
export type SightglassSavedSearchListResult = z.infer<
  typeof sightglassSavedSearchListResultSchema
>;
export type SightglassAdsQuery = z.infer<typeof sightglassAdsQuerySchema>;
export type SightglassSitePagesQuery = z.infer<
  typeof sightglassSitePagesQuerySchema
>;
export type SightglassCompanyWire = z.infer<typeof sightglassCompanyWireSchema>;
export type SightglassCategoryWire = z.infer<
  typeof sightglassCategoryWireSchema
>;
export type SightglassSavedSummaryWire = z.infer<
  typeof sightglassSavedSummaryWireSchema
>;
export type SightglassCompanyListResult = z.infer<
  typeof sightglassCompanyListResultSchema
>;
export type SightglassCompaniesQuery = z.infer<
  typeof sightglassCompaniesQuerySchema
>;
export type SightglassTechnologiesQuery = z.infer<
  typeof sightglassTechnologiesQuerySchema
>;
export type SightglassTechnologyRankWire = z.infer<
  typeof sightglassTechnologyRankWireSchema
>;
export type SightglassTechnologyListResult = z.infer<
  typeof sightglassTechnologyListResultSchema
>;
export type SightglassTechnologyDetailWire = z.infer<
  typeof sightglassTechnologyDetailWireSchema
>;
export type SightglassCompanyTechStackWire = z.infer<
  typeof sightglassCompanyTechStackWireSchema
>;
export type SightglassTechStackItemWire = z.infer<
  typeof sightglassTechStackItemWireSchema
>;
export type SightglassCompanyTrendWire = z.infer<
  typeof sightglassCompanyTrendWireSchema
>;
export type SightglassCompanyLaunchesWire = z.infer<
  typeof sightglassCompanyLaunchesWireSchema
>;
export type SightglassDaysActiveBucketsWire = z.infer<
  typeof sightglassDaysActiveBucketsWireSchema
>;
export type SightglassTopShareWire = z.infer<
  typeof sightglassTopShareWireSchema
>;
export type SightglassCategoryShareWire = z.infer<
  typeof sightglassCategoryShareWireSchema
>;
export type SightglassCategoryLeadersWire = z.infer<
  typeof sightglassCategoryLeadersWireSchema
>;
export type SightglassShareGroupBy = z.infer<
  typeof sightglassShareGroupBySchema
>;
export type SightglassGroupedShareWire = z.infer<
  typeof sightglassGroupedShareWireSchema
>;
export type SightglassMarketPulseWire = z.infer<
  typeof sightglassMarketPulseWireSchema
>;
export type SightglassCompanyFilterOptionsWire = z.infer<
  typeof sightglassCompanyFilterOptionsWireSchema
>;
export type SightglassCompanyUpdateBody = z.infer<
  typeof sightglassCompanyUpdateBodySchema
>;

export const sightglassCampaignCompanyWireSchema = z
  .object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    domain: z.string(),
    logoUrl: z.string().nullable(),
  })
  .describe("SightglassCampaignCompanyWire");

export const sightglassCampaignAdWireSchema = z
  .object({
    id: z.string(),
    title: z.string().nullable(),
    imageUrl: z.string().nullable(),
    displayFormat: z.string(),
    startAt: z.string().nullable(),
    isActive: z.boolean(),
  })
  .describe("SightglassCampaignAdWire");

export const sightglassCampaignPageWireSchema = z
  .object({
    id: z.string(),
    url: z.string(),
    pageType: z.string(),
    screenshotUrl: z.string().nullable(),
  })
  .describe("SightglassCampaignPageWire");

export const sightglassOfferReferenceWireSchema = z
  .object({
    kind: z.enum(["ad", "page"]),
    id: z.string(),
    label: z.string().describe("The ad's title or the page's path, for a link"),
    quote: z
      .string()
      .nullable()
      .describe("The words this source used for the offer"),
  })
  .describe("SightglassOfferReferenceWire");

export const sightglassCampaignStepWireSchema = z
  .object({
    from: z.string(),
    to: z.string().nullable().describe("Null while live"),
    depthPercent: z.number().nullable(),
    amountOff: z.number().nullable(),
    label: z.string().describe("The value alone, e.g. '61% off', '$230 off'"),
    live: z.boolean(),
    adCount: z.number().int(),
    pageCount: z.number().int(),
    note: z
      .string()
      .describe(
        "One plain sentence on where and when this value ran, e.g. 'On 12 ads from May 29, and still running'",
      ),
    references: z
      .array(sightglassOfferReferenceWireSchema)
      .describe("Up to three ads or pages that showed this value"),
  })
  .describe("SightglassCampaignStepWire");

export const sightglassCampaignWireSchema = z
  .object({
    key: z
      .string()
      .describe("company:slot:startDate — stable while the campaign runs"),
    company: sightglassCampaignCompanyWireSchema,
    type: sightglassOfferTypeSchema,
    typeLabel: z.string(),
    mechanic: sightglassOfferMechanicSchema,
    description: z
      .string()
      .describe("Plain English, built from the offer fields"),
    label: z
      .string()
      .describe(
        "The deal alone, short enough for a timeline bar, e.g. 'Labor Day: 30% off everything'",
      ),
    eventLabel: z.string().nullable(),
    depthPercent: z.number().nullable(),
    amountOff: z.number().nullable(),
    quote: z.string().describe("The words the offer was read from"),
    startAt: z.string(),
    endAt: z.string().nullable().describe("Null while live"),
    live: z.boolean(),
    days: z.number().int(),
    adCount: z.number().int(),
    pageCount: z.number().int(),
    steps: z.array(sightglassCampaignStepWireSchema),
    ads: z
      .array(sightglassCampaignAdWireSchema)
      .describe("Up to eight, newest first"),
    pages: z.array(sightglassCampaignPageWireSchema).describe("Up to four"),
    angles: z
      .array(
        z.object({
          key: z.string(),
          name: z.string(),
          type: sightglassAngleTypeSchema,
        }),
      )
      .describe(
        "Angles of the ads and pages that carried this offer, most used first",
      ),
  })
  .describe("SightglassCampaignWire");

export const sightglassCalendarWireSchema = z
  .object({
    from: z.string(),
    to: z.string(),
    companies: z.array(sightglassCampaignCompanyWireSchema),
    campaigns: z.array(sightglassCampaignWireSchema),
  })
  .describe("SightglassCalendarWire");

export const sightglassTopOffersSortSchema = z
  .enum(["ads", "running", "depth"])
  .describe("SightglassTopOffersSort");

export const sightglassTopOffersWireSchema = z
  .object({
    from: z.string(),
    to: z.string(),
    items: z.array(sightglassCampaignWireSchema),
  })
  .describe("SightglassTopOffersWire");

export const sightglassAnglePersonaWireSchema = z
  .object({
    nickname: z.string().describe("Segment label, e.g. 'The weeknight cook'"),
    headline: z
      .string()
      .describe("Situation plus problem in the buyer's own words"),
    ageGte: z.number().int().nullable(),
    ageLt: z.number().int().nullable(),
    ageLabel: z.string().describe("'28 to 44', '55+', 'Any age'"),
    ageBand: sightglassAgeBandSchema,
    awareness: projectPersonaAwarenessSchema.nullable(),
    awarenessLabel: z.string().nullable().describe("'Problem-aware' etc."),
  })
  .describe("SightglassAnglePersonaWire");

export const sightglassPageVersionWireSchema = z
  .object({
    id: z.string(),
    capturedAt: z.string(),
    screenshotMobileUrl: z.string().nullable(),
    screenshotDesktopUrl: z.string().nullable(),
    pageType: sightglassPageTypeSchema
      .nullable()
      .describe("Null when the capture is unread at the current version"),
    summary: z.string().nullable(),
    angle: z
      .object({
        name: z.string(),
        type: sightglassAngleTypeSchema,
        typeLabel: z.string(),
        hook: z.string(),
        persona: sightglassAnglePersonaWireSchema,
      })
      .nullable(),
    offers: z.array(
      z.object({
        description: z.string(),
        label: z.string(),
        type: sightglassOfferTypeSchema,
        typeLabel: z.string(),
        quote: z.string(),
        where: z
          .string()
          .describe(
            "Where on the page it was seen, e.g. 'Top banner', 'Popup'",
          ),
      }),
    ),
    latest: z.boolean(),
  })
  .describe("SightglassPageVersionWire");

export const sightglassPageVersionsWireSchema = z
  .object({ items: z.array(sightglassPageVersionWireSchema) })
  .describe("SightglassPageVersionsWire");

export const sightglassAngleWireSchema = z
  .object({
    key: z
      .string()
      .describe("company:type:normalized name — stable across readings"),
    company: sightglassCampaignCompanyWireSchema,
    type: sightglassAngleTypeSchema,
    typeLabel: z.string(),
    name: z
      .string()
      .describe("The specific idea, e.g. 'Non-toxic nonstick coating'"),
    hook: z.string().describe("The newest headline claim using this angle"),
    persona: sightglassAnglePersonaWireSchema.describe(
      "Who it speaks to, in the Brand Studio persona shape",
    ),
    product: z.string().nullable(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string().nullable().describe("Null while live"),
    live: z.boolean(),
    adCount: z.number().int(),
    activeAdCount: z.number().int(),
    pageCount: z.number().int(),
    offers: z
      .array(z.object({ key: z.string(), label: z.string() }))
      .describe("Offers the same ads and pages carried"),
    ads: z
      .array(sightglassCampaignAdWireSchema)
      .describe("Up to eight, newest first"),
    pages: z.array(sightglassCampaignPageWireSchema).describe("Up to four"),
  })
  .describe("SightglassAngleWire");

export const sightglassTopAnglesSortSchema = z
  .enum(["ads", "pages", "newest"])
  .describe("SightglassTopAnglesSort");

export const sightglassAnglesWireSchema = z
  .object({
    from: z.string(),
    to: z.string(),
    items: z.array(sightglassAngleWireSchema),
  })
  .describe("SightglassAnglesWire");

export const sightglassPersonaWireSchema = z
  .object({
    id: z.string().describe("The persona's id, for saving it to a list"),
    key: z.string().describe("Slug, unique within its project or company"),
    scope: z.enum(["project", "global"]),
    company: sightglassCampaignCompanyWireSchema
      .nullable()
      .describe(
        "The brand a global persona belongs to; null for a project persona",
      ),
    projectId: z.string().nullable(),
    nickname: z.string(),
    headline: z.string(),
    ageGte: z.number().int().nullable(),
    ageLt: z.number().int().nullable(),
    ageLabel: z.string(),
    ageBand: sightglassAgeBandSchema,
    awareness: projectPersonaAwarenessSchema.nullable(),
    awarenessLabel: z.string().nullable(),
    sharePercent: z.number().int().nullable(),
    shareLabel: z
      .string()
      .describe(
        "'Share of buyers' for a project persona, 'Share of ads' for a global one",
      ),
    avatarSvg: z.string(),
    tint: z
      .string()
      .nullable()
      .describe("The avatar background colour, for the card band"),
    color: z
      .string()
      .nullable()
      .describe(
        "The brand colour the avatar was drawn in, for rings and age bars; strong enough to read on white",
      ),
    adCount: z.number().int(),
    pageCount: z.number().int(),
    angles: z
      .array(
        z.object({
          key: z.string(),
          name: z.string(),
          typeLabel: z.string(),
          count: z.number().int(),
        }),
      )
      .describe("Up to five, most used first; empty for a project persona"),
    offers: z
      .array(z.object({ key: z.string(), label: z.string() }))
      .describe("Up to three offers the persona's ads and pages carried"),
    ads: z
      .array(sightglassCampaignAdWireSchema)
      .describe("Up to eight, newest first"),
    pages: z.array(sightglassCampaignPageWireSchema).describe("Up to four"),
  })
  .describe("SightglassPersonaWire");

export const sightglassCompanyPersonasWireSchema = z
  .object({
    companyId: z.string(),
    derivedAt: z
      .string()
      .nullable()
      .describe("When the brand's personas were last built"),
    readingCount: z.number().int(),
    items: z.array(sightglassPersonaWireSchema),
  })
  .describe("SightglassCompanyPersonasWire");

export const sightglassPersonaLaneWireSchema = z
  .object({
    personaKey: z.string().describe("A project persona's key"),
    status: z.enum(["crowded", "contested", "open"]),
    overlaps: z
      .array(z.string())
      .describe(
        "companyId:personaKey of the competitor personas it overlaps, most reach first",
      ),
    reach: z
      .number()
      .int()
      .describe("Ads plus pages across the overlapping personas"),
  })
  .describe("SightglassPersonaLaneWire");

export const sightglassPersonaPlanWireSchema = z
  .object({
    project: z.object({ id: z.string(), name: z.string() }).nullable(),
    projectPersonas: z.array(sightglassPersonaWireSchema),
    companies: z.array(sightglassCampaignCompanyWireSchema),
    personas: z
      .array(sightglassPersonaWireSchema)
      .describe("The companies' global personas"),
    lanes: z.array(sightglassPersonaLaneWireSchema),
    competitorOnly: z
      .array(z.string())
      .describe(
        "companyId:personaKey of competitor personas no project persona overlaps",
      ),
  })
  .describe("SightglassPersonaPlanWire");

export const sightglassListPersonaWireSchema = z
  .object({
    id: z.string(),
    scope: z.enum(["project", "global"]),
    projectId: z
      .string()
      .nullable()
      .describe("The Brand Studio project a project persona belongs to"),
    company: sightglassCampaignCompanyWireSchema
      .nullable()
      .describe(
        "The brand a global persona belongs to; null for a project persona",
      ),
    nickname: z.string(),
    headline: z.string(),
    ageGte: z.number().int().nullable(),
    ageLt: z.number().int().nullable(),
    awareness: projectPersonaAwarenessSchema.nullable(),
    avatarSvg: z.string(),
    boardIds: z.array(z.string()).describe("The caller's lists holding it"),
  })
  .describe("SightglassListPersonaWire");

export const sightglassListPersonasWireSchema = z
  .object({ items: z.array(sightglassListPersonaWireSchema) })
  .describe("SightglassListPersonasWire");

export const sightglassProjectCompanyWireSchema = z
  .object({
    company: sightglassCompanyWireSchema
      .nullable()
      .describe("Null until the project is linked to a Sightglass company"),
    trackedAt: z.string().nullable(),
    researchFinishedAt: z.string().nullable(),
  })
  .describe("SightglassProjectCompanyWire");

export type SightglassListKind = z.infer<typeof sightglassListKindSchema>;
export type SightglassAdType = z.infer<typeof sightglassAdTypeSchema>;
export type SightglassAdTagsWire = z.infer<typeof sightglassAdTagsWireSchema>;
export type SightglassCompanyMix = z.infer<typeof sightglassCompanyMixSchema>;
export type SightglassListPersonaWire = z.infer<
  typeof sightglassListPersonaWireSchema
>;
export type SightglassListPersonasWire = z.infer<
  typeof sightglassListPersonasWireSchema
>;
export type SightglassProjectCompanyWire = z.infer<
  typeof sightglassProjectCompanyWireSchema
>;
